import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'

import type {
    AccountSingleStepPinRequest,
    AddressDetailsRequest,
    CancelTransactionRequest,
    ChangePinRequest,
    CreatePinRequest,
    EmploymentInfoRequest,
    FinancialAccountNumberV2Request,
    FinancialDetailsRequestV2,
    JWTLoginRequest,
    MaritalStatusRequest,
    PersonalDetailsCheckRequest,
    PersonalDetailsV2Request,
    PersonMarketingConsentChangeRequest,
    PreQualifiedInfoRequest,
    ProfileAddressChangeRequest,
    ProfileIbanChangeRequest,
    ReCreatePinRequest,
    RequestFinancialDataRequest,
    ValidationDataV2DTO,
} from '@leanpay/models'
import {
    createStandardRetryHandler,
    STANDARD_RETRY_DELAY_MS,
    useGetCountries as useGetCountriesShared,
    useGetPostZipsByCountry as useGetPostZipsByCountryShared,
} from '@leanpay/query-hooks'
import { checkoutService, getAuthToken, storeTokenAsync } from '@leanpay/services'

import { queryClient } from 'app/app'
import { useAppStore } from 'app/app-store'
import { CacheKey, countryCodebookId, isMarketSlo } from 'library/constants'

/**
 * Reference-data and codebook hooks shared with checkout and self-checkout.
 * Only hooks that need this app's market constant are wrapped here.
 */
export {
    useGetCurrency,
    useGetCurrentTime,
    useGetEmploymentTypes,
    useGetExecutionAmounts,
    useGetMaritalStatuses,
    useGetNumberOfConsecutivePaymentMonths,
    useGetNumberOfRecentLoans,
    useGetPostZipsBySettlement,
    useGetSettlementsByPostZip,
    useGetSettlementsByStreet,
    useGetStreetsByPostZip,
    useGetStreetsBySettlement,
    useLoadMaritalStatus,
} from '@leanpay/query-hooks'

export const useGetCountries = () => useGetCountriesShared(countryCodebookId)
export const useGetPostZipsByCountry = () => useGetPostZipsByCountryShared(countryCodebookId)

export const useMutateTriggerWatchList = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.triggerWatchListCheck(vendorToken),
    })

    return mutation
}

export const useGetStateMachineStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.StateMachineStatus],
        queryFn: () => checkoutService.getStateMachineStatus(vendorToken),
        refetchInterval: 3000,
    })
}

export const useRequestFinancialData = () => {
    const mutation = useMutation({
        mutationFn: (data: RequestFinancialDataRequest) =>
            checkoutService.requestFinancialData(data),
    })
    return mutation
}

export const useLoadFinancialDetails = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.FinancialDetails],
        queryFn: async () => checkoutService.loadFinancialDetails(vendorToken),
        retry: createStandardRetryHandler(),
    })
}

export const useGetSettlements = () => {
    return useQuery({
        queryKey: [CacheKey.Settlement, countryCodebookId],
        enabled: !!countryCodebookId,
        queryFn: async () => {
            const settlements = await checkoutService.getSettlements(countryCodebookId)
            if (isMarketSlo) {
                // Remove duplicated items
                const uniqueItems = new Map()
                const updatedSettlements = settlements.filter((item) => {
                    if (!uniqueItems.has(item.name)) {
                        uniqueItems.set(item.name, true)
                        return true
                    }
                    return false
                })

                return updatedSettlements
            }

            return settlements
        },
    })
}
export const useSubmitMartialStatus = () => {
    const mutation = useMutation({
        mutationFn: (payload: MaritalStatusRequest) => checkoutService.submitMaritalStatus(payload),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useSubmitEmploymentInfo = () => {
    const mutation = useMutation({
        mutationFn: (payload: EmploymentInfoRequest) =>
            checkoutService.submitEmploymentInfo(payload),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}
export const useGetPrequalifiedInfo = (payload: PreQualifiedInfoRequest) => {
    return useQuery({
        enabled: !!payload.token,
        queryKey: [CacheKey.PrequalifiedInfo, payload.token],
        queryFn: () => checkoutService.getPreQualifiedInfo(payload),
    })
}

export const useCheckPersonalDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonalDetailsCheckRequest) => {
            return checkoutService.checkPersonalDetails(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useLoadPersonalDetails = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.loadPersonalDetailsData(vendorToken),
    })

    return mutation
}
export const useRequestPIN = () => {
    const mutation = useMutation({
        mutationFn: async (data: AccountSingleStepPinRequest) => {
            return checkoutService.requestPIN(data)
        },
    })

    return mutation
}

export const useResendOtpOverInvoice = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtpOverVoice(phoneNumber)
        },
    })

    return mutation
}
export const useResendOtp = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtp(phoneNumber)
        },
    })

    return mutation
}

export const useAuthenticate = () => {
    const { setIsAuthenticated } = useAppStore()
    const mutation = useMutation({
        mutationFn: async (data: JWTLoginRequest) => {
            return checkoutService.authenticate(data)
        },
        onSuccess: async (res) => {
            await storeTokenAsync(res.idToken)

            await queryClient.fetchQuery({ queryKey: [CacheKey.SessionInfo] })

            setIsAuthenticated(true)
        },
    })

    return mutation
}

export const useCreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: CreatePinRequest) => {
            return checkoutService.createPin(payload)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useRecreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ReCreatePinRequest) => checkoutService.recreatePin(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useChangePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ChangePinRequest) => checkoutService.changePin(payload),
    })

    return mutation
}

export const useUpdateIban = (twoFaPin: string) => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileIbanChangeRequest) =>
            checkoutService.ibanUpdate(payload, twoFaPin),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateAddress = () => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileAddressChangeRequest) =>
            checkoutService.addressUpdate(payload),

        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateMarketingConsent = () => {
    const mutation = useMutation({
        mutationFn: (payload: PersonMarketingConsentChangeRequest) =>
            checkoutService.changeMarketingConsentLoginAction(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useGetSessionInfo = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        enabled: !!vendorToken && !!getAuthToken(),
        queryKey: [CacheKey.SessionInfo],
        queryFn: () => checkoutService.getSessionInfo(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useSubmitPersonalDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonalDetailsV2Request) => {
            return checkoutService.submitPersonalDetails(data)
        },

        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
        onError: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })
    return mutation
}

export const useSubmitAddressDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: AddressDetailsRequest) => {
            return checkoutService.submitAddressDetailsV2(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}

export const useCheckBankAccountNumber = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialAccountNumberV2Request) =>
            checkoutService.checkBankAccountNumber(request),
        retry: createStandardRetryHandler(),
        retryDelay: STANDARD_RETRY_DELAY_MS,
    })

    return mutation
}
export const useSubmitFinancialDetails = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialDetailsRequestV2) =>
            checkoutService.submitFinancialDetails(request),
    })

    return mutation
}

export const useValidateOfferV2 = () => {
    const mutation = useMutation({
        mutationFn: (data: ValidationDataV2DTO) => {
            return checkoutService.validateCheckoutOfferV2(data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useLoadAddressDetails = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.AddressDetails],
        queryFn: async () => checkoutService.loadAddressDetails(vendorToken),
        retry: createStandardRetryHandler(),
    })
}

export const useCancelTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: CancelTransactionRequest) => {
            return checkoutService.cancelTransaction(data)
        },
    })
    return mutation
}

export const useGetCompaniesListInfiniteScroll = (search: string) => {
    return useInfiniteQuery({
        queryKey: [CacheKey.Companies, search],
        queryFn: ({ pageParam = 0 }) => checkoutService.getCompaniesList(pageParam, 15, search),
        initialPageParam: 0,
        enabled: search.length >= 3,
        getNextPageParam: (lastPage, allPages) => {
            const nextPage = allPages.length
            return nextPage < lastPage.totalPages ? nextPage : undefined
        },
    })
}
